import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Item, ItemContent, ItemTitle, ItemDescription } from '../ui/item';
import { formatPrice } from '../../utils/formatPrice';

const PriceBreakdownModal = ({ isOpen, onClose, placedGraphics, helfoliering = false, helfolieringFee = 10000 }) => {
  // Get all placed graphics across all views
  const allPlacedGraphics = Object.values(placedGraphics).flat();
  
  // Base price - only applied when graphics are placed
  const basePrice = allPlacedGraphics.length > 0 ? 1500 : 0; // Updated to match useCarCustomizer

  // Get unique raster files that need vectorization
  const uniqueRasterFiles = new Set(
    allPlacedGraphics
      .filter(g => {
        // Check if it's an image and has a raster extension
        return g.type === 'image' && 
               g.name && // Make sure name exists
               g.name.toLowerCase().match(/\.(png|jpg|jpeg)$/);
      })
      .map(g => g.originalFileId)
  );

  // Calculate vectorization costs (1500kr per unique raster file)
  const vectorizationCosts = uniqueRasterFiles.size * 1500;

  // Calculate area-based costs
  const calculateGraphicCost = (graphic) => {
    if (graphic.type === 'text') {
      // Set default dimensions for text if not yet set
      const width = graphic.width || 100; // Default width
      const height = graphic.height || 30; // Default height
      return (width * height / 10000) * 250; // Updated: 250kr per 10,000 px²
    }
    const area = graphic.width * graphic.height;
    return (area / 10000) * 250; // Updated: 250kr per 10,000 px²
  };

  // Total graphics cost
  const graphicsCost = allPlacedGraphics.reduce((sum, g) => sum + calculateGraphicCost(g), 0);

  // Helfoliering flat fee (full vehicle wrap)
  const helfolieringCost = helfoliering ? helfolieringFee : 0;

  // Calculate totals (price already includes VAT)
  const totalWithVAT = basePrice + vectorizationCosts + graphicsCost + helfolieringCost;
  const totalBeforeVAT = totalWithVAT / 1.25;
  const VAT = totalWithVAT - totalBeforeVAT;

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Prisuträkning</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {allPlacedGraphics.length > 0 && (
            <Item variant="outline" className="bg-secondary/50 shadow-sm">
              <ItemContent className="w-full">
                <div className="flex justify-between items-center w-full">
                  <ItemTitle>Grundpris</ItemTitle>
                  <span className="font-semibold">{formatPrice(basePrice)} kr</span>
            </div>
              </ItemContent>
            </Item>
          )}

            {uniqueRasterFiles.size > 0 && (
            <Item variant="outline" className="bg-secondary/50 shadow-sm">
              <ItemContent className="w-full">
                <div className="flex justify-between items-center w-full">
              <div>
                    <ItemTitle className="text-primary">
                      Vektorisering ({uniqueRasterFiles.size} {uniqueRasterFiles.size === 1 ? 'rasterfil' : 'rasterfiler'})
                    </ItemTitle>
                    <ItemDescription>
                      Engångskostnad för att konvertera pixelbaserade bilder till vektorgrafik
                    </ItemDescription>
                </div>
                  <span className="font-semibold text-primary">{formatPrice(vectorizationCosts)} kr</span>
              </div>
              </ItemContent>
            </Item>
            )}

            {allPlacedGraphics.length > 0 && (
            <Item variant="outline" className="bg-secondary/50 shadow-sm">
              <ItemContent className="w-full">
                <div className="flex justify-between items-center w-full">
                  <ItemTitle>Bilder ({allPlacedGraphics.length} st)</ItemTitle>
                  <span className="font-semibold">{formatPrice(graphicsCost)} kr</span>
              </div>
              </ItemContent>
            </Item>
            )}

            {helfoliering && (
            <Item variant="outline" className="bg-secondary/50 shadow-sm">
              <ItemContent className="w-full">
                <div className="flex justify-between items-center w-full">
                  <div>
                    <ItemTitle>Helfoliering</ItemTitle>
                    <ItemDescription>
                      Byter hela fordonsfärgen med folie (ingen lackering)
                    </ItemDescription>
                  </div>
                  <span className="font-semibold">{formatPrice(helfolieringFee)} kr</span>
                </div>
              </ItemContent>
            </Item>
            )}

          <Separator />

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Delsumma</span>
              <span>{formatPrice(totalBeforeVAT)} kr</span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moms (25%)</span>
              <span>{formatPrice(VAT)} kr</span>
            </div>

            <Separator />

            <div className="flex justify-between items-center text-lg">
              <span className="font-semibold">Totalt (inkl. moms)</span>
              <span className="font-bold">{formatPrice(totalWithVAT)} kr</span>
            </div>
            </div>
          </div>

        <DialogFooter>
          <Button onClick={onClose}>
            Stäng
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PriceBreakdownModal; 